import React, { useCallback, useState } from 'react';
import {
  selectAudioFromLibrary,
  setGetStartedModalOpen,
  setGiideTabState,
  setPlayingProgress,
  setProcessingRecording,
  unlinkRecordingLocal,
} from '../actions';
import { deleteKey as cacheDeleteKey } from '../components/cache';
import { message } from 'antd';
import useMutation from './useMutation';
import {
  SelectRecordingFromLibraryInput,
  SelectRecordingFromLibraryMutation,
  UnlinkRecordingInput,
  UnlinkRecordingMutation,
} from '../graphql/api';
import {
  selectRecordingFromLibrary,
  unlinkRecording,
} from '../graphql/mutations';
import useTranscribeSubscription from './useSubscription/useTranscribeSubscription';
import useRecording from './useQuery/useRecording';
import useGiide from './useQuery/useGiide';
import { useDispatch } from 'react-redux';
import useGiideAudioLibrary from './useQuery/useGiideAudioLibrary';

const useAudioLibraryOperations = (recordingId, giideId) => {
  const dispatch = useDispatch();
  const { openedStep } = useGiide(giideId);
  const { recording } = useRecording(openedStep?.id);
  const [query, setQuery] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [changing, setChaning] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showChangeAudioModal, setShowChangeAudioModal] =
    React.useState<boolean>(false);
  const { data } = useGiideAudioLibrary();

  const selectRecordingMutation = useMutation<
    SelectRecordingFromLibraryInput,
    SelectRecordingFromLibraryMutation
  >(selectRecordingFromLibrary);

  const unlinkRecordingMutation = useMutation<
    UnlinkRecordingInput,
    UnlinkRecordingMutation
  >(unlinkRecording);

  const { subscribe } = useTranscribeSubscription(openedStep?.order);

  const changeAudio = useCallback(async () => {
    let error = true;
    setChaning(true);
    const timestamp = new Date().toISOString();
    const unlinkProps = { id: recording!.id, timestamp };
    try {
      await unlinkRecordingMutation.callMutation(unlinkProps);
      dispatch(unlinkRecordingLocal(unlinkProps));
      cacheDeleteKey(`public/${recording?.filePath}-get`);
      cacheDeleteKey(`${recording?.transcriptPath}-get`);
      dispatch(setPlayingProgress(0));
      error = false;
    } catch (e) {
      message.error('An error occurred while changing a recording.');
    } finally {
      setChaning(false);
    }
    return error;
  }, [unlinkRecordingMutation, dispatch, recording]);

  const onClickNext = useCallback(async () => {
    try {
      if (selectedId) {
        setLoading(true);

        const duration =
          data?.find(f => f && f.id === selectedId)?.durationSeconds || 0;

        const result = await selectRecordingMutation.callMutation({
          libraryRecordId: selectedId,
          recordingStepId: recording?.deleted ? recordingId : null,
          stepId: recordingId,
          giideId: giideId,
          duration: duration,
        });

        if (!result || !result.data.selectRecordingFromLibrary?.success) {
          throw new Error('An error occured while selecting a recording.');
        }

        dispatch(
          selectAudioFromLibrary(
            recordingId,
            duration * 1000,
            result.data.selectRecordingFromLibrary?.filePath || '',
            'processing'
          )
        );
        dispatch(setProcessingRecording({ state: 'processing' }));
        subscribe();
        setLoading(false);
        setSelectedId(null);
        setQuery(null);
        dispatch(setGetStartedModalOpen('upload'));
      }
    } catch (err) {
      setLoading(false);
      message.error('An error occurred while selecting a recording.');
    }
    if (showChangeAudioModal) setShowChangeAudioModal(false);
  }, [
    selectRecordingMutation,
    selectedId,
    setLoading,
    giideId,
    recordingId,
    data,
    subscribe,
    recording,
    dispatch,
    showChangeAudioModal,
  ]);

  const handleNextClick = useCallback(() => {
    if (
      recording &&
      !recording.deleted &&
      recording.libraryRecordId !== selectedId
    ) {
      setShowChangeAudioModal(true);
    } else if (
      recording &&
      !recording.deleted &&
      recording.libraryRecordId === selectedId
    ) {
      dispatch(setGiideTabState('transcript'));
    } else {
      onClickNext();
    }
  }, [dispatch, onClickNext, recording, selectedId]);

  const handleChangeAudio = useCallback(async () => {
    const changedAudio = await changeAudio();
    if (!changedAudio) onClickNext();
  }, [changeAudio, onClickNext]);

  return {
    audioLibOperations: {
      handleChangeAudio,
      handleNextClick,
      setSelectedId,
      setShowChangeAudioModal,
      setQuery,
    },
    audioLibOperationsData: {
      query,
      loading,
      changing,
      selectedId,
      showChangeAudioModal,
    },
  };
};

export default useAudioLibraryOperations;
